import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { TableSkeleton, Empty, ErrorBanner, Badge, CardHead, SectionHead } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';

/**
 * Voicemail across the account.
 *
 * The per-seat settings live on the seat. This answers the question an
 * administrator actually arrives with: who has a mailbox, where do the
 * messages go, and — the part that matters most — who has none at all.
 */

interface Mailbox {
  id: string; extension_id: string; mailbox_number: string; name: string | null;
  email_to: string[]; email_attach: boolean; delete_after_email: boolean;
  transcribe: boolean; max_message_sec: number;
  greeting_mode: string; greeting_text: string | null; has_pin: boolean;
  extension_number: string; extension_name: string; extension_status: string;
}
interface Seat { id: string; number: string; display_name: string }

export function VoicemailTab({ tenantId, onOpenExtension }: {
  tenantId: string;
  /** Opens the extension; here always at its Voicemail section. */
  onOpenExtension?: (id: string, view?: string) => void;
}) {
  const q = useQuery({
    queryKey: ['voicemail', tenantId],
    queryFn: () => api.get<{ data: Mailbox[]; without_mailbox: Seat[] }>(
      `/v1/tenants/${tenantId}/voicemail`),
    enabled: !!tenantId,
  });

  const boxes = q.data?.data ?? [];
  const without = q.data?.without_mailbox ?? [];
  const noEmail = boxes.filter((b) => b.email_to.length === 0);

  return (
    <>
      <SectionHead title="Voicemail"
        sub="Who has a mailbox, and where the messages go." />

      {q.error && <ErrorBanner error={q.error} />}

      {/* A mailbox nobody is emailed about is a mailbox nobody empties. Worth
          saying before the table, because it is invisible inside it. */}
      {noEmail.length > 0 && (
        <div className="arc-banner arc-banner-warn" style={{ marginBottom: 14 }}>
          <Icon name="warning" size={14} />
          <span>
            {noEmail.length} mailbox{noEmail.length === 1 ? '' : 'es'} send no email
            ({noEmail.map((b) => b.extension_number).join(', ')}). Messages can only
            be heard by dialling in, so they tend to sit there.
          </span>
        </div>
      )}

      <div className="arc-card" style={{ marginBottom: 14 }}>
        <CardHead title="Mailboxes" sub={boxes.length} />
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                <th>Extension</th><th>Mailbox</th><th>Email to</th>
                <th>Greeting</th><th>Options</th>
                <th className="arc-td-num">Max message</th>
              </tr>
            </thead>
            {q.data === undefined ? <TableSkeleton query={q} cols={6} /> : (
              <tbody>
                {boxes.map((b) => (
                  <tr key={b.id} className={onOpenExtension ? 'arc-clickable' : undefined}
                    onClick={() => onOpenExtension?.(b.extension_id, 'voicemail')}>
                    <td>
                      <div className="arc-row" style={{ gap: 10 }}>
                        <span className="arc-ext-num">{b.extension_number}</span>
                        <span className="arc-truncate">{b.extension_name}</span>
                      </div>
                    </td>
                    <td className="arc-mono">{b.mailbox_number}</td>
                    <td className="arc-muted arc-truncate" style={{ maxWidth: 240 }}>
                      {b.email_to.length > 0
                        ? b.email_to.join(', ')
                        : <span className="arc-subtle">nobody</span>}
                    </td>
                    <td className="arc-muted">
                      {b.greeting_mode === 'text' && b.greeting_text
                        ? <span className="arc-truncate">“{b.greeting_text}”</span>
                        : <span className="arc-subtle">{b.greeting_mode}</span>}
                    </td>
                    <td>
                      <div className="arc-row" style={{ gap: 6, flexWrap: 'wrap' }}>
                        {(b.email_attach || b.delete_after_email || b.transcribe) && (
                          <span style={{ fontSize: 12.5 }}>
                            {[
                              b.email_attach && 'attach',
                              b.delete_after_email && 'delete after email',
                              b.transcribe && 'transcribe',
                            ].filter(Boolean).join(', ')}
                          </span>
                        )}
                        {/* A mailbox with no PIN can be opened by anyone who
                            reaches it, including from outside. */}
                        {!b.has_pin && <Badge tone="danger">no PIN</Badge>}
                      </div>
                    </td>
                    <td className="arc-td-num arc-muted">{b.max_message_sec}s</td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
        {q.data !== undefined && boxes.length === 0 && (
          <Empty icon="media" title="No mailboxes"
            hint="Add voicemail from a seat's own page." />
        )}
      </div>

      {without.length > 0 && (
        <div className="arc-card">
          <CardHead title="Seats without voicemail" sub={without.length} />
          <div className="arc-card-body">
            <p className="arc-muted" style={{ fontSize: 12.5, marginTop: 0 }}>
              An unanswered call to these rings out. Nobody can leave a message.
            </p>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {without.map((e) => (
                <button key={e.id} type="button" className="arc-btn arc-btn-sm"
                  onClick={() => onOpenExtension?.(e.id, 'voicemail')}>
                  <span className="arc-mono">{e.number}</span> {e.display_name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
